import { InvalidAboutContentError } from '../errors/InvalidAboutContentError';

export interface TimelineEvent {
  year: number;
  title: string;
  description: string;
}

export interface StatisticItem {
  label: string;
  value: string;
}

export interface Certification {
  title: string;
  issuer: string;
  year: number;
  image: string;
}

export interface StrategicGoal {
  title: string;
  description: string;
}

export interface License {
  title: string;
  description: string;
}

export interface LeadershipMember {
  name: string;
  role: string;
  ownershipPercent: number | null;
}

export interface AboutContentInput {
  introduction: string;
  story: string;
  mission: string;
  vision: string;
  coreValues: string[];
  ceoMessage: string;
  ceoName: string;
  timeline: TimelineEvent[];
  statistics: StatisticItem[];
  whyChooseUs: string[];
  certifications: Certification[];
  strategicGoals: StrategicGoal[];
  licenses: License[];
  leadership: LeadershipMember[];
  ctaLabel: string;
  ctaHref: string;
}

/**
 * Entity for the About page's structured content. Per the pattern in
 * features/home/domain (HeroContent), business rules here are about
 * structural/editorial integrity, not just "is this text non-empty" —
 * e.g. the timeline must be chronologically ordered, since a CMS editor
 * could otherwise submit events out of order without the UI noticing.
 */
export class AboutContent {
  private constructor(public readonly props: AboutContentInput) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: AboutContentInput): AboutContent {
    if (input.introduction.trim().length === 0) {
      throw new InvalidAboutContentError('introduction must not be empty.');
    }
    if (input.mission.trim().length === 0) {
      throw new InvalidAboutContentError('mission must not be empty.');
    }
    if (input.vision.trim().length === 0) {
      throw new InvalidAboutContentError('vision must not be empty.');
    }
    if (input.coreValues.length === 0) {
      throw new InvalidAboutContentError('coreValues must contain at least one value.');
    }
    if (input.timeline.length > 1) {
      for (let i = 1; i < input.timeline.length; i += 1) {
        if (input.timeline[i].year < input.timeline[i - 1].year) {
          throw new InvalidAboutContentError('timeline events must be in chronological order.');
        }
      }
    }
    if (!input.ctaHref.startsWith('/') && !/^https?:\/\//.test(input.ctaHref)) {
      throw new InvalidAboutContentError(
        'ctaHref must be an internal path starting with "/" or an absolute http(s) URL.'
      );
    }
    for (const certification of input.certifications) {
      if (certification.title.trim().length === 0) {
        throw new InvalidAboutContentError('certification title must not be empty.');
      }
      if (certification.year < 1300 || certification.year > 1500) {
        throw new InvalidAboutContentError(
          `certification year ${certification.year} is out of the plausible Persian calendar range (1300-1500).`
        );
      }
    }
    for (const goal of input.strategicGoals) {
      if (goal.title.trim().length === 0) {
        throw new InvalidAboutContentError('strategic goal title must not be empty.');
      }
    }
    for (const license of input.licenses) {
      if (license.title.trim().length === 0) {
        throw new InvalidAboutContentError('license title must not be empty.');
      }
    }
    for (const member of input.leadership) {
      if (member.name.trim().length === 0) {
        throw new InvalidAboutContentError('leadership member name must not be empty.');
      }
      if (member.role.trim().length === 0) {
        throw new InvalidAboutContentError('leadership member role must not be empty.');
      }
      if (member.ownershipPercent !== null && (member.ownershipPercent < 0 || member.ownershipPercent > 100)) {
        throw new InvalidAboutContentError('leadership member ownershipPercent must be between 0 and 100.');
      }
    }

    return new AboutContent(input);
  }
}
